import { useState, type ReactNode } from 'react';

interface Props {
  title: string;
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  onConfirm: (name: string) => void | Promise<void>;
  onCancel: () => void;
  children?: ReactNode;
}

export default function NameDialog({ title, description, placeholder, confirmLabel = 'Create', onConfirm, onCancel, children }: Props) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="w-full max-w-md rounded-2xl border border-gray-200/70 bg-panel p-6 shadow-pop dark:border-white/10 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold tracking-tight dark:text-gray-100">{title}</h2>
        {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        <label className="mb-1.5 mt-5 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Name</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder={placeholder}
          className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/15 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100"
        />
        {children}
        {error && (
          <div role="alert" className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </div>
        )}
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onCancel} className="btn-ghost">
            Cancel
          </button>
          <button onClick={submit} disabled={!name.trim() || busy} className="btn-primary">
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
